import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./useAuth";

/**
 * Hook to check and manage on-demand email extraction for an account.
 *
 * On mount: checks extraction status.
 * If pending: auto-triggers extraction (max 3 attempts).
 * If extracting: polls with backoff (3s → 5s → 10s).
 * When ready: calls onReady callback, then slow-polls (60s) to detect new emails.
 * On ready→pending transition: resets attempts and re-triggers extraction.
 * Pauses polling when tab is hidden, resumes on focus.
 * Stops slow-polling after 30 min of continuous ready state.
 *
 * @param {string} accountId
 * @param {Object} options
 * @param {Function} options.onReady - Called when extraction completes
 * @returns {{ extractionStatus: string, total: number, extracted: number, reason: string|null, lookbackDays: number }}
 */
export const useExtractionStatus = (accountId, { onReady } = {}) => {
  const { apiClient } = useAuth();
  const [extractionStatus, setExtractionStatus] = useState("loading");
  const [total, setTotal] = useState(0);
  const [extracted, setExtracted] = useState(0);
  const [reason, setReason] = useState(null);
  const [lookbackDays, setLookbackDays] = useState(90);

  const triggerAttemptsRef = useRef(0);
  const elapsedRef = useRef(0);
  const readyElapsedRef = useRef(0);
  const timeoutRef = useRef(null);
  const abortRef = useRef(null);
  const onReadyRef = useRef(onReady);
  const mountedRef = useRef(true);
  const prevStatusRef = useRef(null);
  const pollRef = useRef(null);

  // Keep onReady ref current without re-triggering effects
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const getPollingInterval = useCallback(() => {
    const elapsed = elapsedRef.current;
    if (elapsed < 30000) return 3000;
    if (elapsed < 60000) return 5000;
    if (elapsed < 600000) return 10000;
    return 30000; // After 10 min, slow poll — extraction may still run via continuations
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!mountedRef.current || !apiClient || !accountId) return null;

    try {
      const response = await apiClient.get(
        `accounts/${accountId}/extraction-status/`,
        { signal: abortRef.current?.signal },
      );
      return response.data;
    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError")
        return null;
      console.error("Extraction status check failed:", err);
      return null;
    }
  }, [apiClient, accountId]);

  const triggerExtraction = useCallback(async () => {
    if (!mountedRef.current || !apiClient || !accountId) return;
    if (triggerAttemptsRef.current >= 3) return;

    triggerAttemptsRef.current += 1;
    try {
      await apiClient.post(`accounts/${accountId}/extract/`, null, {
        signal: abortRef.current?.signal,
      });
    } catch (err) {
      if (err.name !== "CanceledError" && err.name !== "AbortError") {
        console.error("Extraction trigger failed:", err);
      }
    }
  }, [apiClient, accountId]);

  useEffect(() => {
    mountedRef.current = true;
    abortRef.current = new AbortController();
    triggerAttemptsRef.current = 0;
    elapsedRef.current = 0;
    readyElapsedRef.current = 0;
    prevStatusRef.current = null;

    const poll = async () => {
      if (!mountedRef.current) return;
      // Skip polling when tab is hidden
      if (document.hidden) {
        timeoutRef.current = setTimeout(poll, 5000);
        return;
      }

      const data = await fetchStatus();
      if (!data || !mountedRef.current) return;

      const prevStatus = prevStatusRef.current;
      prevStatusRef.current = data.status;

      setTotal(data.total);
      setExtracted(data.extracted);
      setExtractionStatus(data.status);
      setReason(data.reason || null);
      if (data.lookback_days) setLookbackDays(data.lookback_days);

      if (data.status === "ready") {
        // Notify parent on first ready or transition back to ready
        if (prevStatus !== "ready") {
          readyElapsedRef.current = 0;
          if (onReadyRef.current) {
            onReadyRef.current();
          }
        }

        // Stop slow-polling after 30 min of continuous ready
        readyElapsedRef.current += 60000;
        if (readyElapsedRef.current > 1800000) return;

        // Slow-poll to detect new emails from webhooks
        timeoutRef.current = setTimeout(poll, 60000);
        return;
      }

      // Reset ready elapsed when not in ready state
      readyElapsedRef.current = 0;

      if (data.status === "pending") {
        // Transitioning from ready → pending means new emails arrived
        if (prevStatus === "ready") {
          triggerAttemptsRef.current = 0;
          elapsedRef.current = 0;
        }
        // Auto-trigger extraction
        await triggerExtraction();
        setExtractionStatus("extracting");
      }

      // Schedule next poll with backoff
      const interval = getPollingInterval();
      elapsedRef.current += interval;
      timeoutRef.current = setTimeout(poll, interval);
    };

    pollRef.current = poll;
    poll();

    // Resume polling immediately when tab becomes visible
    const handleVisibility = () => {
      if (!document.hidden && mountedRef.current) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        pollRef.current?.();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [accountId, fetchStatus, triggerExtraction, getPollingInterval]);

  return { extractionStatus, total, extracted, reason, lookbackDays };
};
